import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/race-tracker/components/ui/dialog";
import { Button } from "@/race-tracker/components/ui/button";
import { Input } from "@/race-tracker/components/ui/input";
import { Textarea } from "@/race-tracker/components/ui/textarea";
import { Label } from "@/race-tracker/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Save, Layers } from "lucide-react";
import { base44 } from "@/race-tracker/api/rtClient";

const CHAMBERS = ["U.S. House", "U.S. Senate", "State Senate", "State House", "Governor", "Other"];
const ELECTION_TYPES = ["Primary", "General", "Runoff", "Special"];
const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export default function AddRaceModal({ advertiserId, open, onClose, onSave }) {
  const [form, setForm] = useState({
    state: "", chamber: "", district: "", election_type: "", election_date: "",
    runoff_date: "", markets: "", status: "Active",
  });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkDistricts, setBulkDistricts] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (open) setSaveError("");
  }, [open]);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const parsedDistricts = bulkDistricts
    .split(/[\n,]+/)
    .map((d) => d.trim())
    .filter(Boolean);

  const handleSave = async () => {
    setSaveError("");
    setSaving(true);
    try {
      if (bulkMode && parsedDistricts.length > 0) {
        for (const district of parsedDistricts) {
          await base44.entities.Race.create({ ...form, district, advertiser_id: advertiserId });
        }
      } else {
        await base44.entities.Race.create({ ...form, advertiser_id: advertiserId });
      }
      onSave();
      setForm({ state: "", chamber: "", district: "", election_type: "", election_date: "", runoff_date: "", markets: "", status: "Active" });
      setBulkMode(false);
      setBulkDistricts("");
    } catch (err) {
      setSaveError(err?.message || "Could not add race");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base">Add Race</DialogTitle>
            <Button
              variant={bulkMode ? "default" : "outline"}
              size="sm"
              className="text-xs gap-1 mr-6"
              onClick={() => setBulkMode((v) => !v)}
            >
              <Layers className="w-3 h-3" /> Bulk Districts
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">State *</Label>
              <Select value={form.state} onValueChange={(v) => update("state", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Level of Office *</Label>
              <Select value={form.chamber} onValueChange={(v) => update("chamber", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CHAMBERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!bulkMode && (
              <div className="space-y-1.5">
                <Label className="text-xs">District / Race</Label>
                <Input className="h-8 text-xs" value={form.district} onChange={(e) => update("district", e.target.value)} placeholder="e.g. SD-14" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Election Type</Label>
              <Select value={form.election_type} onValueChange={(v) => update("election_type", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{ELECTION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Primary Date</Label>
              <Input type="date" className="h-8 text-xs" value={form.election_date} onChange={(e) => update("election_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Runoff Date</Label>
              <Input type="date" className="h-8 text-xs" value={form.runoff_date} onChange={(e) => update("runoff_date", e.target.value)} />
            </div>
          </div>
          {bulkMode && (
            <div className="space-y-1.5">
              <Label className="text-xs">Districts (one per line or comma-separated)</Label>
              <Textarea
                className="text-xs min-h-[80px] resize-none font-mono"
                placeholder={"SD-01\nSD-11\nSD-05\nSD-36"}
                value={bulkDistricts}
                onChange={(e) => setBulkDistricts(e.target.value)}
              />
              {parsedDistricts.length > 0 && (
                <p className="text-[10px] text-muted-foreground">{parsedDistricts.length} district{parsedDistricts.length !== 1 ? "s" : ""} will be created</p>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Team</Label>
            <Input className="h-8 text-xs" value={form.team || ""} onChange={(e) => update("team", e.target.value)} placeholder="e.g. pro-Doug Harwell" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Markets</Label>
            <Input className="h-8 text-xs" value={form.markets} onChange={(e) => update("markets", e.target.value)} placeholder="e.g. Mobile, Birmingham" />
          </div>
          {saveError ? <p className="text-xs text-destructive">{saveError}</p> : null}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleSave} disabled={saving || !form.state || !form.chamber || (bulkMode && parsedDistricts.length === 0)}>
              <Save className="w-3 h-3" /> {saving ? "Adding..." : bulkMode ? `Add ${parsedDistricts.length || ""} Races` : "Add Race"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}